import { offerApi, rememberNatOffer, USE_V2_OFFERS_PREVIEW } from './offer-api'
import { Buffer } from 'buffer'
import { address as bitcoinAddress } from 'bitcoinjs-lib'
import { readOffers } from './offer-read'
import { API_URL, OFFERS_API_URL } from './constants'
import Wallet, { ensureEccLib } from './wallet'
import { signOfferPsbts } from './offer-sign'
import { parsePsbt } from './market/secure-psbt'
import { normalizeOutpoint } from './item-listing'
import { OFFER_TERMINAL_STATES, type Offer } from './offers'
import { OfferError, OFFER_PRE_SETTLEMENT_CODES } from './offer-errors'
import { fetchLiveItemOutpoint } from './offer-item'
import { isSaleLeafScript } from './offer-recovery'
import { OFFER_TX_LOCKTIME, OFFER_TX_SEQUENCE, OFFER_TX_VERSION } from './offer-templates'

class OfferApiError extends OfferError {
	constructor(code: string, message: string | undefined, readonly status: number) {
		super(code, message)
	}
}

/** Codes a settlement or refund submit can answer with after it already broadcast. */
const MAYBE_BROADCAST_CODES = [
	'broadcast_failed',
	'broadcast_rejected',
	'store_failed',
	'attempt_store_failed',
	'offer_attempt_pending'
]

/**
 * The API refused before doing anything: a 4xx that is not a broadcast
 * outcome, or an answer that comes before the settlement claim (paused, rate
 * limited, a lookup or the co-signer unavailable, the template or witness
 * could not be built) whatever its status. Anything else may have reached
 * the network, so the outcome stays unknown and the pending mark stays put.
 */
export function isDefinitiveRefusal(error: unknown): error is OfferError {
	if (!(error instanceof OfferError) || typeof error.status !== 'number') return false
	if (MAYBE_BROADCAST_CODES.includes(error.code)) return false
	return OFFER_PRE_SETTLEMENT_CODES.has(error.code) || error.status < 500
}

async function request<T>(path: string, body?: unknown): Promise<T> {
	const res = await fetch(
		`${offerApi(path, body)}${path}${
			body === undefined && offerApi(path, body) !== API_URL
				? `${path.includes('?') ? '&' : '?'}t=${Date.now()}`
				: ''
		}`,
		{
			cache: 'no-store',
			...(body === undefined
				? {}
				: {
						method: 'POST',
						headers: { 'content-type': 'application/json' },
						body: JSON.stringify(body)
				  })
		}
	)
	const data = await res.json()
	if (!res.ok || data?.error)
		throw new OfferApiError(data?.code || `http_${res.status}`, data?.message, res.status)
	return data
}
export const fetchIncomingOffers = async (address: string) => {
	// Report independently broadcast recovery transactions as untrusted hints;
	// the API verifies their on-chain spend before updating the offer.
	try {
		const hints: Array<Promise<unknown>> = []
		for (let i = 0; i < localStorage.length && hints.length < 10; i++) {
			const key = localStorage.key(i)
			if (!key?.startsWith('ow:recovery-submitted:')) continue
			const { txid } = JSON.parse(localStorage.getItem(key)!)
			if (/^[a-f\d]{64}$/i.test(txid))
				hints.push(
					fetch(
						`${offerApi(
							`/market/offers/${encodeURIComponent(
								key.slice('ow:recovery-submitted:'.length)
							)}/reconcile`
						)}/market/offers/${encodeURIComponent(
							key.slice('ow:recovery-submitted:'.length)
						)}/reconcile?txid=${txid}`,
						{ cache: 'no-store', signal: AbortSignal.timeout(5000) }
					)
				)
		}
		void Promise.allSettled(hints)
	} catch {}
	const path = `/market/offers/wallet/${encodeURIComponent(address)}`
	const [primary, nat] = await Promise.all([
		readOffers<{ received: Offer[]; sent: Offer[] }>(path),
		OFFERS_API_URL !== API_URL
			? readOffers<{ received: Offer[]; sent: Offer[] }>(path, OFFERS_API_URL)
			: Promise.resolve({ received: [], sent: [] })
	])
	for (const offer of [...nat.received, ...nat.sent]) rememberNatOffer(offer.id)
	// One offer lives in one API; a shared id across both is the same row.
	const uniq = (offers: Offer[]) => {
		const seen = new Set<string>()
		return offers.filter((o) => (seen.has(o.id) ? false : (seen.add(o.id), true)))
	}
	const data = {
		received: uniq([...primary.received, ...nat.received]),
		sent: uniq([...primary.sent, ...nat.sent])
	}
	for (const offer of [...data.received, ...data.sent]) {
		if (offer.state !== 'building' && offer.funding_txid) {
			try {
				localStorage.removeItem(`ow:pending-offer-funding:v1:${address}:${offer.id}`)
			} catch {}
		}
		if (OFFER_TERMINAL_STATES.has(offer.state)) {
			try {
				localStorage.removeItem(`ow:pending-offer-sale:v1:${address}:${offer.id}`)
				localStorage.removeItem(`ow:pending-offer-refund:v1:${address}:${offer.id}`)
			} catch {}
		}
	}
	return data
}

export async function reconcileOffer(id: string): Promise<Offer> {
	const path = `/market/offers/${encodeURIComponent(id)}/reconcile`
	let result: { offer: Offer }
	try {
		result = await request<{ offer: Offer }>(path)
	} catch (error) {
		if (!(error instanceof OfferApiError) || error.status !== 404 || OFFERS_API_URL === API_URL)
			throw error
		const response = await fetch(`${OFFERS_API_URL}${path}?t=${Date.now()}`, { cache: 'no-store' })
		if (!response.ok) throw error
		result = await response.json()
		rememberNatOffer(id)
	}
	if (result.offer?.nat_terms) rememberNatOffer(id)
	if (result.offer.id !== id) throw new Error('Offer reconciliation returned the wrong offer.')
	return result.offer
}
export const fetchItemOffers = (id: string) =>
	readOffers<{ offers: Offer[]; collection_offers: Offer[] }>(
		`/market/offers/inscription/${encodeURIComponent(id)}`,
		USE_V2_OFFERS_PREVIEW ? OFFERS_API_URL : API_URL
	).then((data) => {
		if (USE_V2_OFFERS_PREVIEW)
			for (const offer of [...data.offers, ...data.collection_offers]) rememberNatOffer(offer.id)
		return data
	})
const ACCEPTED_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
const FIRST_RUN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000
const acceptedSeenKey = (address: string) => `ow:seen-accepted-bids:v1:${address}`

/**
 * The buyer's bids a seller accepted: the buyer now owns (or is about to
 * own) the item. Newest first, limited to the last 30 days so the wallet
 * surfaces do not replay history.
 */
export const acceptedBids = (
	data: { sent?: Offer[] } | undefined,
	address: string,
	now = Date.now()
): Offer[] =>
	(data?.sent || [])
		.filter(
			(o) =>
				o.state === 'accepted' &&
				(o.buyer_address === address || o.buyer_payment_address === address) &&
				now - new Date(o.updated_at).getTime() < ACCEPTED_WINDOW_MS
		)
		.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())

/** The item an accepted bid bought: the filled item for collection/trait bids. */
export const acceptedItemId = (o: Offer) => o.filled_inscription_id || o.inscription_id

/**
 * Which accepted bids the buyer has already been told about. On the first
 * run for a wallet, acceptances older than a week count as seen so a wallet
 * with history is not greeted by a wall of old receipts.
 */
export const readSeenAcceptedBids = (
	address: string,
	accepted: Offer[],
	now = Date.now()
): Set<string> => {
	try {
		const raw = localStorage.getItem(acceptedSeenKey(address))
		if (raw) {
			const value = JSON.parse(raw)
			return new Set(Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [])
		}
	} catch {}
	return new Set(
		accepted
			.filter((o) => now - new Date(o.updated_at).getTime() > FIRST_RUN_WINDOW_MS)
			.map((o) => o.id)
	)
}

export const markSeenAcceptedBids = (address: string, seen: Set<string>, ids: string[]) => {
	const next = new Set([...seen, ...ids])
	try {
		localStorage.setItem(acceptedSeenKey(address), JSON.stringify([...next].slice(-1000)))
		window.dispatchEvent(new Event('ow:accepted-bids-seen'))
	} catch {}
	return next
}

export const isActiveOffer = (o: Offer, now = Date.now()) =>
	o.state === 'active' &&
	(!USE_V2_OFFERS_PREVIEW || o.authorization_version === 2) &&
	Number.isSafeInteger(o.price_sats) &&
	o.price_sats > 0 &&
	Date.parse(o.expires_at) > now
export const formatOfferBtc = (sats: number) =>
	(sats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })
export const offerLabel = (o: Offer) =>
	o.scope === 'item'
		? 'Item offer'
		: o.scope === 'trait'
		? `${o.trait_type}: ${o.trait_value}`
		: 'Collection offer'

export class OfferOutcomeUnknown extends Error {
	constructor() {
		super(
			'The network did not confirm the result. Check the item and offer status before trying again. Do not sign another sale yet.'
		)
	}
}

const pendingKey = (offerId: string) =>
	`ow:pending-offer-sale:v1:${Wallet.getTaprootAddress()}:${offerId}`
/** A sale mark older than this is stale by construction; the API's own claim still guards a double sale. */
export const SETTLEMENT_PENDING_TTL_MS = 6 * 60 * 60 * 1000
/** How long a mark is kept before an idle reconcile may clear it: time for the reconciler to re-send. */
export const SETTLEMENT_PENDING_GRACE_MS = 10 * 60 * 1000

/** When the sale mark was set, or null when there is none. Legacy marks carry no time. */
function settlementPendingSince(offerId: string): number | null {
	try {
		const raw = localStorage.getItem(pendingKey(offerId))
		if (!raw) return null
		if (raw === 'pending') return 0
		const at = Number(JSON.parse(raw)?.at)
		return Number.isFinite(at) ? at : 0
	} catch {
		return null
	}
}

export function offerSettlementPending(offerId: string, now = Date.now()) {
	const since = settlementPendingSince(offerId)
	if (since === null) return false
	// Legacy 'pending' marks have no time; they expire once a reconcile shows nothing in flight.
	if (since > 0 && now - since > SETTLEMENT_PENDING_TTL_MS) {
		setSettlementPending(offerId, false)
		return false
	}
	return true
}
function setSettlementPending(offerId: string, pending: boolean) {
	try {
		if (pending) localStorage.setItem(pendingKey(offerId), JSON.stringify({ at: Date.now() }))
		else localStorage.removeItem(pendingKey(offerId))
	} catch {}
}

/**
 * Called with each reconcile while a sale's outcome is unknown. The mark is
 * cleared once the offer is plainly still open: active, no accepted sale,
 * nothing reported in flight, and the mark old enough that the API's
 * reconciler has re-sent anything it holds. Returns whether it was cleared.
 */
export function clearIdleSettlementMark(
	offer: Offer & { settlement_pending?: boolean },
	reconcile: { settlement_pending?: boolean } = {},
	now = Date.now()
): boolean {
	const since = settlementPendingSince(offer.id)
	if (since === null) return false
	if (
		offer.state !== 'active' ||
		offer.accepted_txid ||
		offer.settlement_pending === true ||
		reconcile.settlement_pending === true ||
		(since > 0 && now - since < SETTLEMENT_PENDING_GRACE_MS)
	)
		return false
	setSettlementPending(offer.id, false)
	return true
}

/** Check the seller's input and proceeds before asking a wallet to sign. */
export function validateOfferSettlement(
	psbtHex: string,
	offer: Offer,
	seller: string,
	outpoint: string,
	postage: number
) {
	const psbt = parsePsbt(psbtHex)
	const sellerScript = bitcoinAddress.toOutputScript(seller)
	const buyerScript = bitcoinAddress.toOutputScript(offer.buyer_address)
	const item = psbt.txInputs[0]
	const funding = psbt.txInputs[1]
	const input = psbt.data.inputs[0]
	const outputs = psbt.txOutputs
	const same = (a: Uint8Array | undefined, b: Uint8Array) =>
		!!a && Buffer.from(a).equals(Buffer.from(b))
	if (
		psbt.inputCount !== 2 ||
		!item ||
		!funding ||
		psbt.version !== OFFER_TX_VERSION ||
		psbt.locktime !== OFFER_TX_LOCKTIME ||
		item.sequence !== OFFER_TX_SEQUENCE ||
		funding.sequence !== OFFER_TX_SEQUENCE ||
		input.sighashType !== 1 ||
		`${Buffer.from(item.hash).reverse().toString('hex')}:${item.index}` !==
			normalizeOutpoint(outpoint) ||
		`${Buffer.from(funding.hash).reverse().toString('hex')}:${funding.index}` !==
			`${offer.funding_txid}:${offer.funding_vout}` ||
		!same(input.witnessUtxo?.script, sellerScript) ||
		input.witnessUtxo?.value !== postage ||
		!same(outputs[0]?.script, buyerScript) ||
		outputs[0]?.value !== postage ||
		!same(outputs[1]?.script, sellerScript) ||
		outputs[1]?.value !== offer.price_sats ||
		outputs.length < (offer.authorization_version === 2 ? 4 : 3) ||
		outputs.length > (offer.authorization_version === 2 ? 5 : 4) ||
		(offer.authorization_version === 2 && !hasOfferDieselMint(outputs)) ||
		outputs[2]?.value !== offer.market_fee_sats ||
		psbt.data.inputs[1]?.witnessUtxo?.value !== offer.escrow_value ||
		outputs.reduce((sum, o) => sum + o.value, 0) > postage + offer.escrow_value
	) {
		throw new Error(
			'The sale transaction does not match this item and offer. Refresh the offer before continuing.'
		)
	}
	return psbt
}

export async function acceptIncomingOffer(
	offer: Offer,
	itemId: string,
	onStep: (step: string) => void,
	options: {
		/** The item output value the seller saw and acknowledged in the review. */
		reviewedPostage?: number
	} = {}
) {
	if (offer.nat_terms) rememberNatOffer(offer.id)
	const seller = Wallet.getTaprootAddress()
	if (offerSettlementPending(offer.id)) throw new OfferOutcomeUnknown()
	if (
		!seller ||
		!isActiveOffer(offer) ||
		offer.buyer_address === seller ||
		(offer.scope === 'item' && (offer.seller_address !== seller || offer.inscription_id !== itemId))
	)
		throw new Error('This offer is not available for your wallet.')
	const requireSameWallet = () => {
		if (Wallet.getTaprootAddress() !== seller)
			throw new Error('The connected wallet changed. Reopen this offer.')
	}
	onStep('Checking item and offer…')
	let live: Awaited<ReturnType<typeof fetchLiveItemOutpoint>>
	try {
		live = await fetchLiveItemOutpoint(itemId)
	} catch {
		throw new Error('Your wallet no longer owns this item, or its ownership could not be verified.')
	}
	if (live.owner !== seller)
		throw new Error('Your wallet no longer owns this item, or its ownership could not be verified.')
	const postage = live.sats
	// The whole item output leaves with the inscription; sign only what was reviewed.
	if (options.reviewedPostage !== undefined && postage !== options.reviewedPostage)
		throw new OfferError('item_output_changed')
	const scoped = offer.scope !== 'item'
	const action = scoped ? 'fill' : 'accept'
	const built = await request<any>(
		`/market/offers/${encodeURIComponent(offer.id)}/build-${action}`,
		{
			seller_address: seller,
			seller_public_key: Wallet.getPublicKey() || undefined,
			...(scoped ? { inscription_id: itemId } : {})
		}
	)
	if (
		built.sign_input_index !== 0 ||
		built.offer?.id !== offer.id ||
		built.offer?.price_sats !== offer.price_sats ||
		built.offer?.buyer_address !== offer.buyer_address ||
		(['protocol', 'ticker', 'token_amount', 'total_price_sats', 'sats_per_nat'] as const).some(
			(key) => built.offer?.nat_terms?.[key] !== offer.nat_terms?.[key]
		) ||
		!isActiveOffer(built.offer) ||
		(scoped && built.inscription_id !== itemId)
	)
		throw new Error('This offer changed. Close the review and refresh it.')
	const hex = scoped ? built.fill_psbt : built.accept_psbt
	await ensureEccLib()
	const psbt = validateOfferSettlement(hex, offer, seller, live.outpoint, postage)
	requireSameWallet()
	onStep('Confirm the sale in your wallet…')
	const [signed] = await signOfferPsbts([{ psbt: hex, indexes: [0] }])
	requireSameWallet()
	if (
		!psbt.data.globalMap.unsignedTx
			.toBuffer()
			.equals(parsePsbt(signed).data.globalMap.unsignedTx.toBuffer())
	)
		throw new Error('The wallet changed the sale transaction. Nothing was submitted.')
	onStep('Submitting sale…')
	setSettlementPending(offer.id, true)
	try {
		const result = await request<{ txid: string; pending?: boolean }>(
			`/market/offers/${encodeURIComponent(offer.id)}/${action}`,
			{ seller_address: seller, signed_psbt: signed, ...(scoped ? { inscription_id: itemId } : {}) }
		)
		if (!/^[a-f\d]{64}$/i.test(result.txid)) throw new OfferOutcomeUnknown()
		setSettlementPending(offer.id, !!result.pending)
		return result
	} catch (error) {
		// A transport/server failure can occur after broadcast. Never invite a blind retry.
		if (isDefinitiveRefusal(error)) {
			setSettlementPending(offer.id, false)
			throw error
		}
		throw new OfferOutcomeUnknown()
	}
}

export async function declineIncomingOffer(offer: Offer) {
	const seller = Wallet.getTaprootAddress()
	if (!seller || offer.scope !== 'item' || offer.seller_address !== seller || !isActiveOffer(offer))
		throw new Error('This offer cannot be declined by your wallet.')
	const challenge = await request<{ nonce: string; message: string; expires_at: string }>(
		`/market/offers/${encodeURIComponent(offer.id)}/build-reject`,
		{}
	)
	const expiry = Math.floor(Date.parse(challenge.expires_at) / 1000)
	const expected = `Ordinals Wallet offer action v2\nNetwork: bitcoin-mainnet\nAction: decline\nOffer: ${offer.id}\nAddress: ${seller}\nNonce: ${challenge.nonce}\nExpires: ${expiry}`
	if (
		!/^[0-9a-f-]{36}$/i.test(challenge.nonce) ||
		challenge.message !== expected ||
		expiry <= Date.now() / 1000 ||
		expiry > Date.now() / 1000 + 660
	)
		throw new Error('Invalid decline authorization. Reload this offer.')
	const { signature, success } = await Wallet.signMessage(expected)
	if (!success || !signature) throw new Error('Decline authorization was not signed.')
	if (Wallet.getTaprootAddress() !== seller)
		throw new Error('The connected wallet changed. Reopen this offer.')
	return request(`/market/offers/${encodeURIComponent(offer.id)}/reject`, {
		address: seller,
		nonce: challenge.nonce,
		signature
	})
}

export const canRefundOffer = (offer: Offer) =>
	['active', 'rejected', 'expired', 'stale', 'settling', 'cancelling'].includes(offer.state) &&
	!!offer.funding_txid &&
	offer.funding_vout != null &&
	offer.escrow_value > 0

export interface OfferRefund {
	psbt: string
	feeRate: number
	feeSats: number
	refundSats: number
}
const refundKey = (offerId: string) =>
	`ow:pending-offer-refund:v1:${Wallet.getTaprootAddress()}:${offerId}`
export function offerRefundPending(offerId: string) {
	try {
		return localStorage.getItem(refundKey(offerId)) === 'pending'
	} catch {
		return false
	}
}
export class RefundOutcomeUnknown extends Error {
	constructor() {
		super(
			'The refund result could not be confirmed. Check the offer and your wallet before trying again. Do not sign another refund yet.'
		)
	}
}
function requireRefundOwner(offer: Offer) {
	const buyer = Wallet.getTaprootAddress()
	if (!buyer || buyer !== offer.buyer_address || !canRefundOffer(offer))
		throw new Error('This offer cannot be cancelled by your wallet.')
	if (![buyer, Wallet.balanceAddress()].includes(offer.buyer_payment_address))
		throw new Error('Connect the original payment wallet to refund this offer.')
}

/** Pin the sole escrow input, original payment destination, and displayed refund fee. */
export function validateOfferRefund(hex: string, offer: Offer, feeRate: number): OfferRefund {
	const psbt = parsePsbt(hex)
	const input = psbt.txInputs[0]
	const output = psbt.txOutputs[0]
	const feeSats = offer.escrow_value - (output?.value ?? 0)
	// Preserve legacy refund quotes; v2 adds the vault mint to its larger leaf.
	// Exact versioned bounds prevent an arbitrary fee or output being substituted.
	const budgets =
		offer.authorization_version === 2 ? [180 + OFFER_DIESEL_OUTPUT_VBYTES] : [160, 170]
	const validBudget = budgets.some((vbytes) => feeSats === Math.ceil(vbytes * feeRate))
	const refundSats = offer.escrow_value - feeSats
	if (
		!Number.isFinite(feeRate) ||
		feeRate < 1 ||
		feeRate > 500 ||
		!validBudget ||
		!Number.isSafeInteger(refundSats) ||
		refundSats < 546 ||
		psbt.inputCount !== 1 ||
		// The buyer signs this escrow input: it may carry only the 2-of-2 sale leaf.
		psbt.data.inputs[0].tapLeafScript?.length !== 1 ||
		!isSaleLeafScript(psbt.data.inputs[0].tapLeafScript[0].script) ||
		psbt.txOutputs.length !== (offer.authorization_version === 2 ? 2 : 1) ||
		(offer.authorization_version === 2 && !hasOfferDieselMint(psbt.txOutputs)) ||
		`${Buffer.from(input.hash).reverse().toString('hex')}:${input.index}` !==
			`${offer.funding_txid}:${offer.funding_vout}` ||
		psbt.data.inputs[0].sighashType !== 1 ||
		psbt.data.inputs[0].witnessUtxo?.value !== offer.escrow_value ||
		output.value !== refundSats ||
		!Buffer.from(output.script).equals(bitcoinAddress.toOutputScript(offer.buyer_payment_address))
	)
		throw new Error('The refund transaction does not match this offer, payment address, or fee.')
	return { psbt: hex, feeRate, feeSats, refundSats }
}

export async function buildOfferRefund(offer: Offer, feeRate: number): Promise<OfferRefund> {
	requireRefundOwner(offer)
	if (offerRefundPending(offer.id) && offer.state !== 'cancelling') throw new RefundOutcomeUnknown()
	if (!Number.isFinite(feeRate) || feeRate < 1 || feeRate > 500)
		throw new Error('Choose a fee from 1 to 500 sat/vB.')
	if (offer.nat_terms) rememberNatOffer(offer.id)
	const built = await request<any>(`/market/offers/${encodeURIComponent(offer.id)}/build-cancel`, {
		buyer_address: offer.buyer_address,
		fee_rate: feeRate
	})
	requireRefundOwner(offer)
	if (
		built.offer_id !== offer.id ||
		built.sign_input_index !== 0 ||
		built.tapscript !== true ||
		built.sighash !== 1 ||
		built.fee_rate !== feeRate
	)
		throw new Error('The refund changed. Refresh this offer and try again.')
	await ensureEccLib()
	return validateOfferRefund(built.cancel_psbt, offer, feeRate)
}

export async function cancelSentOffer(
	offer: Offer,
	reviewed: OfferRefund,
	onStep: (step: 'checking' | 'signing' | 'submitting') => void,
	signal?: AbortSignal
) {
	signal?.throwIfAborted()
	requireRefundOwner(offer)
	onStep('checking')
	const fresh = await buildOfferRefund(offer, reviewed.feeRate)
	signal?.throwIfAborted()
	const original = parsePsbt(reviewed.psbt).data.globalMap.unsignedTx.toBuffer()
	if (!original.equals(parsePsbt(fresh.psbt).data.globalMap.unsignedTx.toBuffer()))
		throw new Error('The refund changed. Close this review and check the updated amounts.')
	onStep('signing')
	const [signed] = await signOfferPsbts(
		[{ psbt: fresh.psbt, indexes: [0], tapscript: true }],
		signal
	)
	signal?.throwIfAborted()
	requireRefundOwner(offer)
	if (!original.equals(parsePsbt(signed).data.globalMap.unsignedTx.toBuffer()))
		throw new Error('The wallet changed the refund transaction. Nothing was submitted.')
	const key = refundKey(offer.id)
	onStep('submitting')
	try {
		localStorage.setItem(key, 'pending')
	} catch {}
	try {
		const result = await request<{
			txid: string
			state: string
			offer_id: string
			pending?: boolean
		}>(`/market/offers/${encodeURIComponent(offer.id)}/cancel`, {
			buyer_address: offer.buyer_address,
			signed_psbt: signed
		})
		if (
			result.offer_id !== offer.id ||
			!['cancelled', 'cancelling'].includes(result.state) ||
			(result.state === 'cancelling' && result.pending !== true) ||
			!/^[a-f\d]{64}$/i.test(result.txid)
		)
			throw new RefundOutcomeUnknown()
		try {
			if (!result.pending) localStorage.removeItem(key)
		} catch {}
		return result
	} catch (error) {
		if (isDefinitiveRefusal(error)) {
			try {
				localStorage.removeItem(key)
			} catch {}
			throw error
		}
		throw new RefundOutcomeUnknown()
	}
}

import { hasOfferDieselMint, OFFER_DIESEL_OUTPUT_VBYTES } from './offer-diesel'
